import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  FolderKanban, CalendarDays, Settings,
  Plus, IndianRupee, TrendingUp, AlertTriangle, ArrowLeft, Clock,
  CheckCircle, Circle, Edit2, Trash2, Save, Loader2,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileBarChart, Upload, Download,
  Search, Camera, Share2, FileSpreadsheet, Link2, BarChart3, Activity,
  MoreVertical, Filter, ChevronDown, X, RefreshCw, Eye,
  ReceiptIndianRupee, ListChecks, Bell, Send, Mail, MessageCircle, LogOut, FileText, Sparkles, Users, History
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import CollectionAnalytics from "@/components/CollectionAnalytics";
import CashFlowForecast from "@/components/collection/CashFlowForecast";
import SalesTeamDashboard from "@/components/collection/SalesTeamDashboard";
import SalesTeamSettings from "@/components/collection/SalesTeamSettings";
import FxRateCard from "@/components/collection/FxRateCard";
import ClosingReport from "@/components/collection/ClosingReport";
import InvoicesPanel from "@/components/collection/InvoicesPanel";
import CompanyProfileSettings from "@/components/collection/CompanyProfileSettings";
import EmailSettings from "@/components/collection/EmailSettings";
import AutoReminderSettings from "@/components/collection/AutoReminderSettings";
import RemindersSettings from "@/components/collection/RemindersSettings";
import StatementDocument from "@/components/collection/StatementDocument";
import ReconcilePanel from "@/components/collection/ReconcilePanel";

import ProjectSearch from "@/pages/collection-planning/ProjectSearch";
import PaginationBar from "@/pages/collection-planning/PaginationBar";
import ProjectsBulkEditDialog from "@/pages/collection-planning/ProjectsBulkEditDialog";
import ReportsPanel from "@/pages/collection-planning/ReportsPanel";
import WeeklyPlanTab from "@/pages/collection-planning/WeeklyPlanTab";
import DailyReceiptsTab from "@/pages/collection-planning/DailyReceiptsTab";
import SettingsTab from "@/pages/collection-planning/SettingsTab";
import {
  LOGO_URL, BILLING_TYPES, BILLING_FREQUENCIES, validateBillingStart, validateBillingEnd,
  REVENUE_STREAMS, STATUS_MAP, fmt, fmtFull, sanitizeNumeric,
  _fmtDate, _addDays, _getProjectAmount, getDatePreset, PRESETS, RECEIPT_PRESETS,
  allowedFrequenciesForType, defaultFrequencyForType,
} from "@/pages/collection-planning/planningUtils";







export default function CollectionPlanningPage() {
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem('user_data') || '{}').role === 'admin'; } catch { return false; } })();
  const handleSignOut = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    window.location.href = '/login';
  };
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user_data') || '{}');
      return u.role === 'admin' ? "analytics" : "projects";
    } catch { return "analytics"; }
  });
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectStatusFilter, setProjectStatusFilter] = useState("active"); // active | all | completed | on_hold | cancelled
  const [projectTypeFilter, setProjectTypeFilter] = useState([]); // selected keys
  const [loading, setLoading] = useState(true);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ name: "", project_type: "erp", status: "active", currency: "INR", order_date: "", start_date: "", project_amount: 0, notes: "", purchase_order_url: "", billing_lines: [], contact_name: "", contact_email: "", contact_phone: "", client_name: "", client_gstin: "", client_state: "", client_state_code: "", client_address: "", client_phone: "", client_email: "", gst_category: "B2B" });
  const [saving, setSaving] = useState(false);

  // Weekly state
  const [weekStart, setWeekStart] = useState(() => { const n = new Date(); const m = new Date(n); m.setDate(n.getDate() - n.getDay() + 1); return m.toISOString().split("T")[0]; });
  // On first mount, snap to the earliest open week (if any older week has un-closed entries)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/collections/planner/earliest-open-week");
        const eow = r.data?.week_start;
        if (!cancelled && eow && eow < weekStart) setWeekStart(eow);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [entryForm, setEntryForm] = useState({ project_id: "", date: "", planned_amount: 0, notes: "", billing_line_id: "" });
  const [weeklyView, setWeeklyView] = useState("week"); // 'week' or 'list'
  const [allEntries, setAllEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesPageSize, setEntriesPageSize] = useState(25);

  // Daily / Receipts
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split("T")[0]);
  const [receipts, setReceipts] = useState([]);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [receiptForm, setReceiptForm] = useState({ project_id: "", receipt_date: "", collection_amount: 0, bank_reference: "", notes: "", billing_line_id: "", sales_member_id: "", fx_rate_to_inr: null });
  // Daily view kept as 'list' only (Day view removed in v2)
  const [dailyView, setDailyView] = useState("list");
  const [receiptsSubview, setReceiptsSubview] = useState("receipts"); // "receipts" | "reconcile"
  const [listReceipts, setListReceipts] = useState([]);
  const [listFilters, setListFilters] = useState(() => {
    // Default: Current Week (Monday → today)
    const today = new Date();
    const dow = today.getDay();
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    const weekStartDt = new Date(today.getFullYear(), today.getMonth(), today.getDate() + diffToMonday);
    const fmt = (d) => d.toISOString().split("T")[0];
    return { start: fmt(weekStartDt), end: fmt(today), project_id: "", q: "" };
  });
  const [listLoading, setListLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(25);

  // Bank Statement
  const [bankLines, setBankLines] = useState([]);
  const [bankMappings, setBankMappings] = useState({});
  const [bankBillingLineMappings, setBankBillingLineMappings] = useState({});
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Snapshot
  const [snapshotData, setSnapshotData] = useState(null);
  const [showSnapshot, setShowSnapshot] = useState(false);

  // Manual reminder dialog
  const [reminderDialog, setReminderDialog] = useState({ open: false, project: null, template_id: "", channel: "email", override_email: "", override_phone: "", billing_line_id: "" });
  const [reminderTemplates, setReminderTemplates] = useState([]);
  const [sendingReminder, setSendingReminder] = useState(false);

  // Sales Team
  const [salesMembers, setSalesMembers] = useState([]);
  const loadSalesMembers = useCallback(async () => {
    try {
      const r = await api.get("/collections/sales-team?active=true");
      setSalesMembers(r.data || []);
    } catch { /* silent */ }
  }, []);

  // Currency / FX rate (admin-configured default USD→INR)
  const [defaultFxRate, setDefaultFxRate] = useState(83.0);
  const loadFxRate = useCallback(async () => {
    try {
      const r = await api.get("/collections/fx-rate");
      setDefaultFxRate(r.data?.usd_inr || 83.0);
    } catch { /* silent */ }
  }, []);

  // Weekly Plan — outstanding sidebar + DnD
  const [outstandingItems, setOutstandingItems] = useState([]);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);
  const [outstandingFilter, setOutstandingFilter] = useState("");
  const [draggingItem, setDraggingItem] = useState(null);
  const loadOutstanding = useCallback(async () => {
    setLoadingOutstanding(true);
    try {
      const r = await api.get("/collections/planner/outstanding?weeks_ahead=13");
      setOutstandingItems(r.data?.items || []);
    } catch { /* silent */ }
    setLoadingOutstanding(false);
  }, []);

  const handleDropOnDay = async (dayIso, ev) => {
    ev.preventDefault();
    // Case 1: moving an existing weekly entry between days
    if (draggingEntry) {
      const entry = draggingEntry;
      setDraggingEntry(null);
      await moveEntryToDay(entry, dayIso);
      return;
    }
    // Case 2: dropping an outstanding item (existing flow)
    if (!draggingItem) return;
    const it = draggingItem;
    setDraggingItem(null);
    // Suggest planned amount = amount_inr (INR-normalized) so the weekly gap math is correct
    const plannedAmount = Math.max(0, Math.round((it.pending_prior && it.pending_prior > 0) ? it.pending_prior : it.amount_inr));
    try {
      await api.post("/collections/entries", {
        project_id: it.project_id,
        date: dayIso,
        planned_amount: plannedAmount,
        billing_line_id: it.billing_line_id || "",
        notes: `Planned from ${it.type || ""} ${it.frequency || ""} — bill due ${it.next_bill_date}`,
      });
      toast.success(`${it.project_name} → ${dayIso}`);
      loadWeeklyPlan(); loadOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add entry");
    }
  };

  const bulkAutoPlan = async () => {
    try {
      const r = await api.post("/collections/planner/bulk-auto-plan", { week_start: weekStart });
      toast.success(r.data.message || "Auto-planned");
      loadWeeklyPlan(); loadOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Auto-plan failed");
    }
  };

  const [closingReport, setClosingReport] = useState(null);
  const [pastReports, setPastReports] = useState([]);
  const [showPastReports, setShowPastReports] = useState(false);
  const loadPastReports = useCallback(async () => {
    try {
      const r = await api.get("/collections/planner/closing-reports");
      setPastReports(r.data || []);
    } catch { /* silent */ }
  }, []);
  const openPastReport = async (week_start) => {
    try {
      const r = await api.get(`/collections/planner/closing-report/${week_start}`);
      setShowPastReports(false);
      setClosingReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load report");
    }
  };

  const closeWeek = async () => {
    // Count what will happen
    const entries = Object.values(weeklyPlan?.entries_by_date || {}).flat();
    const openCount = entries.filter(e => !e.closed && (e.collected_from_receipts || 0) < (e.planned_amount || 0)).length;
    const msg = openCount > 0
      ? `Close this week?\n\n${entries.length} entries in total.\n${openCount} unreceived — they will be carried forward to next week (with the shortfall amount).`
      : "All entries in this week are fully received. Close it anyway?";
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post("/collections/planner/close-week", { week_start: weekStart });
      toast.success(r.data.message || "Week closed");
      if (r.data.report) setClosingReport(r.data.report);  // show closing report dialog
      // Advance to next week
      if (r.data.next_week_start) setWeekStart(r.data.next_week_start);
      loadWeeklyPlan(); loadOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Close-week failed");
    }
  };

  // Cross-reference: billing lines already scheduled anywhere in this week are hidden from outstanding
  const plannedBillingLineIds = (() => {
    const s = new Set();
    const entries = Object.values(weeklyPlan?.entries_by_date || {}).flat();
    for (const e of entries) {
      if (e.billing_line_id) s.add(e.billing_line_id);
    }
    return s;
  })();

  // Move an existing weekly entry to a different day (or remove it entirely)
  const [draggingEntry, setDraggingEntry] = useState(null);
  const moveEntryToDay = async (entry, newDate) => {
    if (!entry || !newDate || entry.date === newDate) return;
    try {
      await api.put(`/collections/entries/${entry.id}`, { date: newDate });
      toast.success(`Moved to ${newDate}`);
      loadWeeklyPlan(); loadOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    }
  };
  const removeEntry = async (entry) => {
    if (!entry) return;
    if (!window.confirm(`Remove ${entry.project_name} from ${entry.date}? The billing line will reappear in Outstanding.`)) return;
    try {
      await api.delete(`/collections/entries/${entry.id}`);
      toast.success("Removed — back in Outstanding");
      loadWeeklyPlan(); loadOutstanding();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Remove failed");
    }
  };

  // Week is "OPEN" if any entry is not closed. Blocks navigation to next week.
  const hasOpenEntries = (() => {
    const entries = Object.values(weeklyPlan?.entries_by_date || {}).flat();
    return entries.some(e => !e.closed);
  })();
  const guardedNextWeek = () => {
    if (hasOpenEntries) {
      toast.error("Close current week first — unreceived entries will be carried forward.", { duration: 4000 });
      return;
    }
    nextWeek();
  };

  const [targets, setTargets] = useState({ daily_target: 250000, weekly_target: 1250000, monthly_target: 5000000, annual_target: 60000000 });

  // FY Targets
  const [fyTargets, setFyTargets] = useState([]);
  const [fyForm, setFyForm] = useState({ fy_start_year: new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1, daily_target: 0, weekly_target: 0, monthly_target: 0, annual_target: 0 });
  const [editingFy, setEditingFy] = useState(null);

  const loadFyTargets = useCallback(async () => {
    try { const r = await api.get("/collections/fy-targets"); setFyTargets(r.data || []); } catch {}
  }, []);
  const handleSaveFy = async () => {
    setSaving(true);
    try {
      if (editingFy) {
        await api.put(`/collections/fy-targets/${editingFy.id}`, fyForm);
        toast.success("FY target updated");
      } else {
        await api.post("/collections/fy-targets", { ...fyForm, fy_label: `FY ${fyForm.fy_start_year}-${String(fyForm.fy_start_year + 1).slice(-2)}` });
        toast.success("FY target added");
      }
      setEditingFy(null);
      setFyForm({ fy_start_year: new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1, daily_target: 0, weekly_target: 0, monthly_target: 0, annual_target: 0 });
      loadFyTargets();
      setAnalytics(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const handleDeleteFy = async (id) => {
    if (!window.confirm("Delete this FY target?")) return;
    try { await api.delete(`/collections/fy-targets/${id}`); toast.success("Deleted"); loadFyTargets(); setAnalytics(null); }
    catch { toast.error("Delete failed"); }
  };
  const handleEditFy = (fy) => {
    setEditingFy(fy);
    setFyForm({ fy_start_year: fy.fy_start_year, daily_target: fy.daily_target, weekly_target: fy.weekly_target, monthly_target: fy.monthly_target, annual_target: fy.annual_target });
  };

  // Data Eraser (bulk-delete receipts by date range)
  const [eraseRange, setEraseRange] = useState({ start: "", end: "" });
  const [erasePreview, setErasePreview] = useState(null); // { count, total_amount }
  const [erasePreviewLoading, setErasePreviewLoading] = useState(false);
  const [eraseConfirmOpen, setEraseConfirmOpen] = useState(false);
  const [erasing, setErasing] = useState(false);
  const erasePreviewRef = useRef(null);

  // Unambiguous date formatter for display (e.g. "1 Mar 2026") — avoids US/UK MM/DD vs DD/MM confusion
  const fmtDateLong = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(`${iso}T00:00:00`);
      if (isNaN(d)) return iso;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  const applyEraserQuickRange = (key) => {
    const now = new Date();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    let start, end;
    if (key === "currentFY") {
      // Indian FY Apr-Mar
      const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      start = iso(new Date(y, 3, 1));
      end = iso(new Date(y + 1, 2, 31));
    } else if (key === "currentMonth") {
      start = iso(new Date(now.getFullYear(), now.getMonth(), 1));
      end = iso(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    } else if (key === "last90") {
      const s = new Date(); s.setDate(s.getDate() - 89);
      start = iso(s); end = iso(now);
    } else if (key === "allTime") {
      start = "1970-01-01"; end = "2099-12-31";
    }
    setEraseRange({ start, end });
    setErasePreview(null);
  };

  const handleErasePreview = async () => {
    if (!eraseRange.start || !eraseRange.end) { toast.error("Select both start and end dates"); return; }
    if (eraseRange.start > eraseRange.end) { toast.error("Start date cannot be after end date"); return; }
    setErasePreviewLoading(true); setErasePreview(null);
    try {
      const r = await api.get(`/collections/receipts/bulk-delete-preview?start_date=${eraseRange.start}&end_date=${eraseRange.end}`);
      setErasePreview(r.data);
      // Scroll the preview panel into view so the result is visible below the button
      setTimeout(() => {
        erasePreviewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    setErasePreviewLoading(false);
  };

  const handleEraseConfirm = async () => {
    setErasing(true);
    try {
      const r = await api.delete(`/collections/receipts/bulk?start_date=${eraseRange.start}&end_date=${eraseRange.end}`);
      toast.success(`Erased ${r.data.deleted} receipt${r.data.deleted === 1 ? "" : "s"}`);
      setErasePreview(null);
      setEraseRange({ start: "", end: "" });
      setEraseConfirmOpen(false);
      // Refresh any open lists / analytics
      setAnalytics(null);
      if (activeTab === "daily") { loadReceipts(); loadListReceipts(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erase failed"); }
    setErasing(false);
  };

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [fyComparison, setFyComparison] = useState(null);
  const [yoyData, setYoyData] = useState(null);
  const [revRec, setRevRec] = useState(null);
  const [selectedFys, setSelectedFys] = useState([]); // start years
  const [aiInsights, setAiInsights] = useState("");
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);

  const loadDashboard = useCallback(() => { setAnalytics(null); }, []);
  const loadProjects = useCallback(async () => { try { const r = await api.get("/collections/projects"); setProjects(r.data || []); } catch {} }, []);
  const loadTargets = useCallback(async () => { try { const r = await api.get("/collections/targets"); setTargets(r.data); } catch {} }, []);
  const loadAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const [dashRes, yoyRes, revRecRes] = await Promise.all([
        api.get("/collections/analytics/dashboard"),
        api.get("/collections/analytics/yoy-comparison"),
        api.get("/collections/analytics/revenue-recognition"),
      ]);
      setAnalytics(dashRes.data);
      setYoyData(yoyRes.data);
      setRevRec(revRecRes.data);
    } catch { toast.error("Failed to load analytics"); }
    setLoadingAnalytics(false);
  }, []);
  const loadFyComparison = useCallback(async (fys) => {
    if (!fys || fys.length === 0) { setFyComparison(null); return; }
    setLoadingAnalytics(true);
    try { const r = await api.get(`/collections/analytics/fy-comparison?fys=${fys.join(",")}`); setFyComparison(r.data); }
    catch { toast.error("Failed to load FY comparison"); }
    setLoadingAnalytics(false);
  }, []);
  const generateAiInsights = useCallback(async () => {
    setLoadingAi(true); setAiInsights("");
    try { const r = await api.post("/collections/analytics/ai-insights"); setAiInsights(r.data.insights); }
    catch (e) { toast.error(e.response?.data?.detail || "AI insights failed — check Admin > AI Settings"); }
    setLoadingAi(false);
  }, []);

  useEffect(() => { (async () => { setLoading(true); await Promise.all([loadDashboard(), loadProjects(), loadTargets(), loadSalesMembers(), loadFxRate()]); setLoading(false); })(); }, []); // eslint-disable-line

  const loadWeeklyPlan = useCallback(async () => { try { const r = await api.get(`/collections/planner/weekly?week_start=${weekStart}`); setWeeklyPlan(r.data); } catch {} }, [weekStart]);
  const loadAllEntries = useCallback(async () => {
    setEntriesLoading(true);
    try { const r = await api.get("/collections/entries"); setAllEntries(r.data || []); }
    catch { toast.error("Failed to load entries"); }
    setEntriesLoading(false);
  }, []);
  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this planned entry?")) return;
    try { await api.delete(`/collections/entries/${id}`); toast.success("Deleted"); loadAllEntries(); loadWeeklyPlan(); }
    catch { toast.error("Delete failed"); }
  };
  const editEntry = (entry) => {
    setEntryForm({ id: entry.id, project_id: entry.project_id, date: entry.date, planned_amount: entry.planned_amount || 0, notes: entry.notes || "", billing_line_id: entry.billing_line_id || "" });
    setShowEntryDialog(true);
  };
  const loadReceipts = useCallback(async () => {
    try { const r = await api.get(`/collections/receipts?start_date=${dailyDate}&end_date=${dailyDate}`); setReceipts(r.data || []); } catch {}
  }, [dailyDate]);
  const loadListReceipts = useCallback(async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      if (listFilters.start) params.set("start_date", listFilters.start);
      if (listFilters.end) params.set("end_date", listFilters.end);
      if (listFilters.project_id) params.set("project_id", listFilters.project_id);
      const r = await api.get(`/collections/receipts?${params.toString()}`);
      setListReceipts(r.data || []);
    } catch { toast.error("Failed to load receipts"); }
    setListLoading(false);
  }, [listFilters.start, listFilters.end, listFilters.project_id]);

  // Reset pagination when dataset / filters change
  useEffect(() => { setEntriesPage(1); }, [allEntries.length]);
  useEffect(() => { setListPage(1); }, [listReceipts.length, listFilters.q]);

  useEffect(() => { if (activeTab === "weekly" && weeklyView === "week") { loadWeeklyPlan(); loadOutstanding(); } }, [activeTab, weeklyView, loadWeeklyPlan, loadOutstanding]);
  useEffect(() => { if (activeTab === "weekly" && weeklyView === "list") loadAllEntries(); }, [activeTab, weeklyView, loadAllEntries]);
  useEffect(() => { if (activeTab === "daily" && dailyView === "day") loadReceipts(); }, [activeTab, dailyView, loadReceipts]);
  useEffect(() => { if (activeTab === "daily" && dailyView === "list") loadListReceipts(); }, [activeTab, dailyView, loadListReceipts]);
  useEffect(() => { if (activeTab === "analytics" && !analytics) loadAnalytics(); }, [activeTab, analytics, loadAnalytics]);
  useEffect(() => { if (activeTab === "analytics" && fyTargets.length === 0) loadFyTargets(); }, [activeTab, fyTargets.length, loadFyTargets]);
  useEffect(() => { if (activeTab === "settings") loadFyTargets(); }, [activeTab, loadFyTargets]);

  const fileInputRef = useRef(null);
  const handleProjectsImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/collections/projects/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} projects`);
      loadProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
  };
  const downloadProjectsExport = async () => {
    try {
      const res = await api.get("/collections/projects/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `projects_export_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Projects exported");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed — please retry");
    }
  };
  const downloadProjectsTemplate = () => { window.open(`${api.defaults.baseURL}/collections/templates/projects`, "_blank"); };

  const filteredProjects = projects.filter(p => {
    if (projectSearch && !p.name.toLowerCase().includes(projectSearch.toLowerCase())) return false;
    if (projectTypeFilter.length > 0 && !projectTypeFilter.includes(p.project_type)) return false;
    if (projectStatusFilter !== "all" && (p.status || "active") !== projectStatusFilter) return false;
    return true;
  });

  // Project CRUD
  const handleSaveProject = async () => {
    if (!projectForm.name) { toast.error("Project name required"); return; }
    // AMC guard: AMC billing lines require the project to be Completed
    const hasAmc = (projectForm.billing_lines || []).some(bl => (bl.type || "").toLowerCase() === "amc");
    if (hasAmc && (projectForm.status || "active") !== "completed") {
      toast.error("AMC billing lines require project status = 'Completed'. Change status or remove the AMC line.");
      return;
    }
    setSaving(true);
    try {
      if (editingProject) { await api.put(`/collections/projects/${editingProject.id}`, projectForm); toast.success("Updated"); }
      else { await api.post("/collections/projects", projectForm); toast.success("Created"); }
      setShowProjectDialog(false); setEditingProject(null); loadProjects(); loadDashboard();
    } catch (e) { toast.error("Failed"); }
    setSaving(false);
  };
  const handleDeleteProject = async (id) => {
    if (!window.confirm("Delete project and all its data?")) return;
    try { await api.delete(`/collections/projects/${id}`); toast.success("Deleted"); loadProjects(); loadDashboard(); } catch { toast.error("Failed"); }
  };

  // Weekly Entry
  const handleSaveEntry = async () => {
    if (!entryForm.project_id || !entryForm.date || !entryForm.planned_amount) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try {
      if (entryForm.id) {
        const { id, ...payload } = entryForm;
        await api.put(`/collections/entries/${id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/collections/entries", entryForm);
        toast.success("Entry added");
      }
      setShowEntryDialog(false); loadWeeklyPlan(); loadDashboard();
      if (weeklyView === "list") loadAllEntries();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save planned entry"); }
    setSaving(false);
  };

  // Receipt
  const [editingReceipt, setEditingReceipt] = useState(null);
  const openNewReceipt = () => {
    setEditingReceipt(null);
    setReceiptForm({ project_id: "", receipt_date: new Date().toISOString().split("T")[0], collection_amount: 0, bank_reference: "", notes: "", billing_line_id: "", sales_member_id: "", fx_rate_to_inr: null, allocations: [] });
    setShowReceiptDialog(true);
  };
  const openEditReceipt = (r) => {
    setEditingReceipt(r);
    setReceiptForm({
      project_id: r.project_id || "",
      receipt_date: (r.receipt_date || "").slice(0, 10),
      collection_amount: r.collection_amount || 0,
      bank_reference: r.bank_reference || "",
      notes: r.notes || "",
      billing_line_id: r.billing_line_id || "",
      sales_member_id: r.sales_member_id || "",
      fx_rate_to_inr: r.fx_rate_to_inr ?? null,
      allocations: Array.isArray(r.allocations) ? r.allocations.map(a => ({ billing_line_id: a.billing_line_id, amount: a.amount })) : [],
    });
    setShowReceiptDialog(true);
  };
  const handleSaveReceipt = async () => {
    if (!receiptForm.project_id || !receiptForm.receipt_date || !receiptForm.collection_amount) { toast.error("Fill all fields"); return; }
    if (!receiptForm.sales_member_id) { toast.error("Please select a Sales Team Member"); return; }
    // If the selected project has billing lines, a billing line link is MANDATORY —
    // either a single `billing_line_id` OR one/more `allocations` rows.
    const selProj = projects.find(pp => pp.id === receiptForm.project_id);
    const hasProjectLines = (selProj?.billing_lines || []).length > 0;
    const allocs = (receiptForm.allocations || []).filter(a => a.billing_line_id && Number(a.amount) > 0);
    if (hasProjectLines && !receiptForm.billing_line_id && allocs.length === 0) {
      toast.error("Billing Line is required. Pick a single line, or use 'Add Line' below to split across multiple.");
      return;
    }
    if (allocs.length > 0) {
      const seen = new Set();
      for (const a of allocs) {
        if (seen.has(a.billing_line_id)) { toast.error("Duplicate billing line in allocations"); return; }
        seen.add(a.billing_line_id);
      }
      const total = allocs.reduce((s, a) => s + Number(a.amount || 0), 0);
      if (Math.abs(Number(total) - Number(receiptForm.collection_amount)) > 0.01) {
        toast.error(`Split total (₹${total.toLocaleString("en-IN")}) must equal receipt amount (₹${Number(receiptForm.collection_amount).toLocaleString("en-IN")})`);
        return;
      }
      // When multiple allocations, each must have a sales owner (fallback to receipt-level owner if blank)
      if (allocs.length > 1) {
        for (const a of allocs) {
          if (!a.sales_member_id) a.sales_member_id = receiptForm.sales_member_id;
        }
      }
    }
    setSaving(true);
    try {
      const payload = { ...receiptForm, allocations: allocs };
      if (editingReceipt) {
        await api.put(`/collections/receipts/${editingReceipt.id}`, payload);
        toast.success("Receipt updated");
      } else {
        await api.post("/collections/receipts", payload);
        toast.success("Receipt recorded");
      }
      setShowReceiptDialog(false);
      setEditingReceipt(null);
      loadReceipts(); loadListReceipts(); loadDashboard(); loadProjects();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const deleteReceipt = async (id) => {
    try { await api.delete(`/collections/receipts/${id}`); toast.success("Deleted"); loadReceipts(); loadListReceipts(); loadDashboard(); } catch { toast.error("Failed"); }
  };

  const exportReceiptsExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (listFilters.dateStart) params.set("date_start", listFilters.dateStart);
      if (listFilters.dateEnd) params.set("date_end", listFilters.dateEnd);
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("auth_token");
      const res = await fetch(`${backend}/api/collections/receipts-export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stem = [listFilters.dateStart, listFilters.dateEnd].filter(Boolean).join("_to_") || "all";
      a.download = `receipts_${stem}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  // Bank statement upload
  const handleBankUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = ""; // Reset input for re-upload
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/collections/bank-statement/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBankLines(r.data.lines || []); setShowBankDialog(true);
      const mappings = {};
      r.data.lines.forEach(l => {
        if (l.suggested_project_id) mappings[l.id] = l.suggested_project_id;
      });
      setBankMappings(mappings);
      setBankBillingLineMappings({});
      toast.success(`${r.data.total} lines loaded`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed — check file format"); }
  };

  const handleBankMap = async () => {
    const mappings = Object.entries(bankMappings).filter(([_, pid]) => pid).map(([lid, pid]) => ({ line_id: lid, project_id: pid, billing_line_id: bankBillingLineMappings[lid] || "" }));
    if (mappings.length === 0) { toast.error("Map at least one line"); return; }
    setSaving(true);
    try {
      const r = await api.post("/collections/bank-statement/map", mappings);
      toast.success(`${r.data.created} receipts created`); setShowBankDialog(false); loadReceipts(); loadListReceipts(); loadDashboard(); loadProjects();
    } catch { toast.error("Mapping failed"); }
    setSaving(false);
  };

  // Historical import
  const handleHistoricalImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/collections/receipts/import-historical", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} receipts. Skipped: ${r.data.skipped}`);
      if (r.data.errors?.length > 0) toast.error(`Errors: ${r.data.errors.slice(0, 3).join(", ")}`);
      setShowImportDialog(false); loadReceipts(); loadListReceipts(); loadDashboard(); loadProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed — check file format"); }
  };

  // Weekly Plan Template Download (auth-protected — must use api blob)
  const downloadWeeklyTemplate = async () => {
    try {
      const r = await api.get("/collections/templates/weekly-plan", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "weekly_plan_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill Date & Planning Amount, then upload back");
    } catch { toast.error("Template download failed"); }
  };

  const handleWeeklyImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/collections/planner/weekly/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} entries. Skipped: ${r.data.skipped}`);
      if (r.data.errors?.length > 0) {
        r.data.errors.slice(0, 3).forEach(err => toast.error(err));
      }
      loadWeeklyPlan(); loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || "Weekly import failed"); }
  };

  // Snapshot
  const loadSnapshot = async (projectId) => {
    try { const r = await api.get(`/collections/projects/${projectId}/snapshot`); setSnapshotData(r.data); setShowSnapshot(true); } catch { toast.error("Failed"); }
  };

  // Render the currently visible ERP Statement into a PDF and download it.
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const _renderStatementToPdf = async (fileNameBase) => {
    const el = document.getElementById("statement-document");
    if (!el) { toast.error("Statement layout not ready"); return; }
    const [html2canvasMod, jsPdfMod] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    // Temporarily bring the element into view for canvas rendering
    const prevTop = el.style.top;
    el.style.top = "0";
    el.style.left = "-99999px"; // still off-screen horizontally
    const canvas = await html2canvasMod.default(el, { scale: 2, backgroundColor: "#ffffff", useCORS: true, allowTaint: true, logging: false });
    el.style.top = prevTop; el.style.left = "0";
    const imgData = canvas.toDataURL("image/png");
    const jsPDF = jsPdfMod.jsPDF;
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgProps = pdf.getImageProperties(imgData);
    const imgW = pageW; // full-bleed
    const imgH = (imgProps.height * imgW) / imgProps.width;
    let heightLeft = imgH;
    let position = 0;
    pdf.addImage(imgData, "PNG", 0, position, imgW, imgH, undefined, "FAST");
    heightLeft -= pageH;
    while (heightLeft > 0) {
      position = -imgH + heightLeft; // shift image up
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgW, imgH, undefined, "FAST");
      heightLeft -= pageH;
    }
    const safeName = (fileNameBase || "project").replace(/[^a-z0-9]+/gi, "_").slice(0, 40);
    pdf.save(`${safeName}_statement_${(new Date()).toISOString().slice(0, 10)}.pdf`);
  };

  // Download PDF Statement for a project (from Projects list). Loads snapshot data first,
  // then renders the hidden statement layout, rasterises, and downloads.
  const downloadStatementPDF = async (projectId, projectName) => {
    setGeneratingPdf(true);
    try {
      const r = await api.get(`/collections/projects/${projectId}/snapshot`);
      setSnapshotData(r.data);
      // Wait a paint tick so the hidden statement document renders with new data
      await new Promise(res => setTimeout(res, 600));
      await _renderStatementToPdf(projectName);
      toast.success("Statement PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
      console.error(e);
    }
    setGeneratingPdf(false);
  };

  const openReminderDialog = async (project) => {
    try {
      const tpls = (await api.get("/collections/reminders/templates")).data || [];
      setReminderTemplates(tpls);
      const firstEmail = tpls.find(t => t.channel === "email");
      const firstWA = tpls.find(t => t.channel === "whatsapp");
      const initialChannel = firstEmail ? "email" : (firstWA ? "whatsapp" : "email");
      const initialTpl = (initialChannel === "email" ? firstEmail : firstWA)?.id || "";
      setReminderDialog({
        open: true,
        project,
        template_id: initialTpl,
        channel: initialChannel,
        override_email: "",
        override_phone: "",
        billing_line_id: "",
      });
    } catch { toast.error("Failed to load templates"); }
  };

  const sendManualReminder = async () => {
    if (!reminderDialog.template_id) { toast.error("Select a template"); return; }
    setSendingReminder(true);
    try {
      const body = {
        project_id: reminderDialog.project.id,
        billing_line_id: reminderDialog.billing_line_id || null,
        template_id: reminderDialog.template_id,
        channel: reminderDialog.channel,
        override_email: reminderDialog.override_email || null,
        override_phone: reminderDialog.override_phone || null,
      };
      await api.post("/collections/reminders/send-manual", body);
      toast.success("Reminder sent successfully");
      setReminderDialog({ ...reminderDialog, open: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    setSendingReminder(false);
  };

  const handleSaveTargets = async () => {
    setSaving(true);
    try { await api.put("/collections/targets", targets); toast.success("Targets saved"); loadDashboard(); } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const TABS = (() => {
    let allTabs = [
      { id: "analytics", label: "Analytics", icon: Activity, adminOnly: true },
      { id: "cashflow", label: "Cash Flow", icon: TrendingUp, adminOnly: true },
      { id: "sales_team", label: "Sales Team", icon: Users, adminOnly: true },
      { id: "projects", label: "Projects", icon: FolderKanban },
      { id: "weekly", label: "Weekly Plan", icon: CalendarDays },
      { id: "daily", label: "Receipts", icon: ReceiptIndianRupee },
      { id: "invoices", label: "Invoices", icon: FileText },
      { id: "reports", label: "Reports", icon: FileBarChart },
      { id: "settings", label: "Settings", icon: Settings, adminOnly: true },
    ];
    // Hide admin-only tabs for non-admin users who have collections access
    try {
      const u = JSON.parse(localStorage.getItem('user_data') || '{}');
      if (u.role !== 'admin') allTabs = allTabs.filter(t => !t.adminOnly);
    } catch { /* keep all */ }
    return allTabs;
  })();

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d.toISOString().split("T")[0]); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d.toISOString().split("T")[0]); };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="collection-planning">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 fixed h-full z-30 hidden lg:block">
        <div className="p-4 border-b border-slate-100">
          <Link to="/"><img src={LOGO_URL} alt="Absolin" className="h-8 object-contain" /></Link>
          <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-semibold">Collection Planning</p>
        </div>
        <nav className="p-3 space-y-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === t.id ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-4 left-0 right-0 px-4">
          {isAdmin ? (
            <Link to="/admin-portal" className="flex items-center gap-2 text-xs text-slate-400 hover:text-[#0066CC]" data-testid="sidebar-back-to-admin"><ArrowLeft className="w-3.5 h-3.5" /> Back to Admin</Link>
          ) : (
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-xs text-slate-400 hover:text-red-600 transition-colors"
              data-testid="sidebar-sign-out"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          )}
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b z-30 px-4 py-3">
        <div className="flex items-center gap-2"><img src={LOGO_URL} alt="Absolin" className="h-6" /><span className="text-xs font-semibold text-slate-500">Collections</span></div>
        <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${activeTab === t.id ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600"}`}><t.icon className="w-3.5 h-3.5" /> {t.label}</button>
          ))}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 lg:ml-56 pt-24 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">

          {/* PROJECTS */}
          {activeTab === "projects" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Project Master</h1>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="projects-actions-btn">
                      Actions <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={() => { setEditingProject(null); setProjectForm({ name: "", project_type: "erp", status: "active", currency: "INR", order_date: "", start_date: "", project_amount: 0, notes: "", purchase_order_url: "", billing_lines: [], contact_name: "", contact_email: "", contact_phone: "", client_name: "", client_gstin: "", client_state: "", client_state_code: "", client_address: "", client_phone: "", client_email: "", gst_category: "B2B" }); setShowProjectDialog(true); }} data-testid="action-add-project">
                      <Plus className="w-4 h-4 mr-2" /> Add Project
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowBulkEdit(true)} data-testid="action-bulk-edit-projects">
                      <ListChecks className="w-4 h-4 mr-2" /> Bulk Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={downloadProjectsExport} data-testid="action-export-projects">
                      <Download className="w-4 h-4 mr-2" /> Export Projects
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => fileInputRef.current?.click()} data-testid="action-import-projects">
                      <Upload className="w-4 h-4 mr-2" /> Import Projects
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadProjectsTemplate} data-testid="action-template-projects">
                      <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Template
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleProjectsImport} />
              </div>

              {/* Search + Type Filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input className="pl-9 h-10" placeholder="Search by project name..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} data-testid="project-search-input" />
                  {projectSearch && (
                    <button onClick={() => setProjectSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded">
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  )}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2 h-10" data-testid="project-type-filter-btn">
                      <Filter className="w-3.5 h-3.5" />
                      Type {projectTypeFilter.length > 0 && <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{projectTypeFilter.length}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2" align="end">
                    <div className="flex items-center justify-between px-2 py-1.5">
                      <p className="text-xs font-semibold text-slate-600">Filter by Type</p>
                      {projectTypeFilter.length > 0 && (
                        <button onClick={() => setProjectTypeFilter([])} className="text-[10px] text-blue-600 hover:underline">Clear</button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto space-y-0.5 mt-1">
                      {REVENUE_STREAMS.filter(s => !s.legacy).map(s => (
                        <label key={s.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer text-sm">
                          <Checkbox
                            checked={projectTypeFilter.includes(s.key)}
                            onCheckedChange={(checked) => {
                              setProjectTypeFilter(prev => checked ? [...prev, s.key] : prev.filter(k => k !== s.key));
                            }}
                            data-testid={`filter-type-${s.key}`}
                          />
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                          <span className="flex-1">{s.label}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                {/* Status Filter Chips */}
                <div className="flex items-center gap-1" data-testid="project-status-filter">
                  {[
                    { id: "active", label: "Active" },
                    { id: "on_hold", label: "On Hold" },
                    { id: "completed", label: "Completed" },
                    { id: "cancelled", label: "Cancelled" },
                    { id: "all", label: "All" },
                  ].map(s => (
                    <button
                      key={s.id}
                      data-testid={`project-status-${s.id}`}
                      onClick={() => setProjectStatusFilter(s.id)}
                      className={`px-2.5 py-1.5 text-[11px] rounded-full border transition ${projectStatusFilter === s.id ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 whitespace-nowrap">{filteredProjects.length} of {projects.length}</p>
              </div>

              <div className="grid gap-3">
                {filteredProjects.map(p => (
                  <Card key={p.id} className="border-slate-200 hover:border-blue-200 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900 text-sm">{p.name}</h3>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : p.status === "completed" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">{REVENUE_STREAMS.find(s => s.key === p.project_type)?.label || p.project_type}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Amount: <strong className="text-slate-700">{fmt(_getProjectAmount(p))}</strong></p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs text-slate-400">Collected</p>
                          <p className="text-sm font-bold text-emerald-600">{fmt(p.total_collected || 0)}</p>
                          <p className="text-[10px] text-slate-400">Due: <span className="text-red-600 font-semibold">{fmt(p.outstanding || 0)}</span></p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                          setEditingProject(p); setProjectForm({ name: p.name, project_type: p.project_type, status: p.status || "active", currency: p.currency || "INR", order_date: p.order_date || "", start_date: p.start_date || "", project_amount: _getProjectAmount(p), notes: p.notes || "", purchase_order_url: p.purchase_order_url || "", billing_lines: p.billing_lines || [], contact_name: p.contact_name || "", contact_email: p.contact_email || "", contact_phone: p.contact_phone || "", client_name: p.client_name || "", client_gstin: p.client_gstin || "", client_state: p.client_state || "", client_state_code: p.client_state_code || "", client_address: p.client_address || "", client_phone: p.client_phone || "", client_email: p.client_email || "", gst_category: p.gst_category || "B2B" }); setShowProjectDialog(true);
                        }}><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
                        {p.purchase_order_url && /^https?:\/\//.test(p.purchase_order_url) && (
                          <a href={p.purchase_order_url} target="_blank" rel="noopener noreferrer" data-testid={`project-po-link-${p.id}`}>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-[#0066CC] hover:bg-blue-50"><Eye className="w-3 h-3 mr-1" /> View PO</Button>
                          </a>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:bg-emerald-50" onClick={() => loadSnapshot(p.id)}><Camera className="w-3 h-3 mr-1" /> Snapshot</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-violet-700 hover:bg-violet-50" onClick={() => downloadStatementPDF(p.id, p.name)} disabled={generatingPdf} data-testid={`download-statement-btn-${p.id}`}>
                          <FileText className="w-3 h-3 mr-1" /> {generatingPdf ? "Preparing…" : "Statement PDF"}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600 hover:bg-amber-50" onClick={() => openReminderDialog(p)} data-testid={`send-reminder-btn-${p.id}`}><Bell className="w-3 h-3 mr-1" /> Send Reminder</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => handleDeleteProject(p.id)}><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {projects.length === 0 && <p className="text-center text-slate-400 py-12">No projects yet.</p>}
                {projects.length > 0 && filteredProjects.length === 0 && <p className="text-center text-slate-400 py-12">No projects match your filters.</p>}
              </div>
            </div>
          )}

          {/* WEEKLY PLANNER */}
          {activeTab === "weekly" && (
            <WeeklyPlanTab ctx={{
              weeklyView, setWeeklyView,
              weekStart, weeklyPlan, hasOpenEntries,
              outstandingItems, outstandingFilter, setOutstandingFilter, loadingOutstanding, plannedBillingLineIds,
              draggingItem, setDraggingItem, draggingEntry, setDraggingEntry,
              allEntries, entriesLoading, entriesPage, setEntriesPage, entriesPageSize, setEntriesPageSize,
              setShowEntryDialog, setEntryForm, setShowPastReports,
              downloadWeeklyTemplate, handleWeeklyImport, bulkAutoPlan, closeWeek, loadPastReports,
              prevWeek, guardedNextWeek, handleDropOnDay, removeEntry, loadOutstanding,
              editEntry, deleteEntry,
            }} />
          )}

          {/* DAILY RECEIPTS */}
          {activeTab === "daily" && (
            <DailyReceiptsTab ctx={{
              receiptsSubview, setReceiptsSubview,
              listFilters, setListFilters, listReceipts, listLoading,
              listPage, setListPage, listPageSize, setListPageSize,
              showReceiptDialog, setShowReceiptDialog, receiptForm, setReceiptForm,
              editingReceipt, setEditingReceipt,
              projects, salesMembers, defaultFxRate, saving,
              setShowImportDialog,
              openNewReceipt, openEditReceipt, handleSaveReceipt, deleteReceipt,
              exportReceiptsExcel, handleBankUpload, loadListReceipts,
            }} />
          )}

          {/* REPORTS */}
          {activeTab === "reports" && <ReportsPanel projects={projects} onSnapshot={loadSnapshot} />}
          {activeTab === "invoices" && <InvoicesPanel />}

          {/* ANALYTICS */}
          {activeTab === "analytics" && (
            <CollectionAnalytics
              analytics={analytics}
              fyComparison={fyComparison}
              yoyComparison={yoyData}
              revRec={revRec}
              selectedFys={selectedFys}
              fyTargets={fyTargets}
              loading={loadingAnalytics}
              loadingAi={loadingAi}
              aiInsights={aiInsights}
              onRefresh={loadAnalytics}
              onGenerateAi={generateAiInsights}
              onFysChange={(fys) => { setSelectedFys(fys); loadFyComparison(fys); }}
            />
          )}

          {/* CASH FLOW FORECAST */}
          {activeTab === "cashflow" && <CashFlowForecast />}

          {/* SALES TEAM DASHBOARD */}
          {activeTab === "sales_team" && <SalesTeamDashboard />}

          {/* SETTINGS */}
          {activeTab === "settings" && (
            <SettingsTab ctx={{
              targets, setTargets, saving, handleSaveTargets,
              fyForm, setFyForm, editingFy, setEditingFy, fyTargets, handleSaveFy, handleEditFy, handleDeleteFy,
              eraseRange, setEraseRange, erasePreview, setErasePreview,
              erasePreviewLoading, handleErasePreview, applyEraserQuickRange, setEraseConfirmOpen,
              erasePreviewRef, fmtDateLong,
              defaultFxRate, loadFxRate,
            }} />
          )}
        </div>
      </main>

      {/* Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-[1100px] w-[95vw] max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{editingProject ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Project Name *</Label><Input value={projectForm.name} onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. ABC Corp ERP" /></div>
            <div><Label className="text-xs">Type</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={projectForm.project_type} onChange={e => setProjectForm(p => ({ ...p, project_type: e.target.value }))} data-testid="project-type-select">
                {REVENUE_STREAMS.filter(s => !s.legacy).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                {/* Show legacy value if currently editing a project that has one */}
                {editingProject && REVENUE_STREAMS.find(s => s.key === projectForm.project_type)?.legacy && (
                  <option value={projectForm.project_type}>{REVENUE_STREAMS.find(s => s.key === projectForm.project_type)?.label}</option>
                )}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Order Date</Label><Input type="date" value={projectForm.order_date} onChange={e => setProjectForm(p => ({ ...p, order_date: e.target.value }))} /></div>
              <div><Label className="text-xs">Start Date <span className="text-slate-400 font-normal">(billing start — drives revenue)</span></Label><Input type="date" value={projectForm.start_date} onChange={e => setProjectForm(p => ({ ...p, start_date: e.target.value }))} /></div>
            </div>
            <div>
              <Label className="text-xs">Project Status</Label>
              <select
                className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={projectForm.status || "active"}
                onChange={e => setProjectForm(p => ({ ...p, status: e.target.value }))}
                data-testid="project-status-select"
              >
                <option value="active">Active</option>
                <option value="on_hold">On Hold</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <p className="text-[10px] text-slate-400 mt-1">AMC billing lines can be added only on <strong>Completed</strong> projects.</p>
            </div>
            <div>
              <Label className="text-xs">Currency *</Label>
              <select
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={projectForm.currency}
                onChange={e => setProjectForm(p => ({ ...p, currency: e.target.value }))}
                data-testid="project-currency-select"
                disabled={!!editingProject && (projectForm.billing_lines?.length > 0 || projectForm.project_amount > 0)}
              >
                <option value="INR">INR — Indian Rupee (₹)</option>
                <option value="USD">USD — US Dollar ($)</option>
              </select>
              <p className="text-[10px] text-slate-400 mt-1">
                {editingProject
                  ? "Currency cannot be changed once a project has billing lines or receipts."
                  : "All billing lines and receipts on this project will use this currency."}
              </p>
            </div>
            <div><Label className="text-xs">Project Amount ({projectForm.currency}) *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">{projectForm.currency === "USD" ? "$" : "₹"}</span>
                <Input type="text" inputMode="numeric" autoComplete="off" className="pl-8 h-11 text-base font-medium" value={projectForm.project_amount ? String(projectForm.project_amount) : ""} onFocus={(e) => e.target.select()} onChange={e => { const c = sanitizeNumeric(e.target.value); setProjectForm(p => ({ ...p, project_amount: c === "" ? 0 : parseInt(c, 10) })); }} placeholder="0" data-testid="project-amount-input" />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                {projectForm.currency === "USD"
                  ? <>≈ ${((projectForm.project_amount || 0) / 1000).toFixed(1)} K · <span className="text-[#0066CC] font-medium">≈ ₹{new Intl.NumberFormat("en-IN").format(Math.round((projectForm.project_amount || 0) * defaultFxRate))}</span> <span className="text-slate-300">@ ₹{defaultFxRate}</span></>
                  : `≈ ₹${((projectForm.project_amount || 0) / 100000).toFixed(2)} L`}
              </p>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={projectForm.notes} onChange={e => setProjectForm(p => ({ ...p, notes: e.target.value }))} rows={2} /></div>

            {/* ===== AR Contact (used by reminders) ===== */}
            <div className="border border-blue-100 rounded-lg p-3 bg-blue-50/40">
              <p className="text-[11px] font-semibold text-slate-700 mb-2">AR Contact — used for automated reminders</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div><Label className="text-[10px]">Contact Name</Label><Input value={projectForm.contact_name} onChange={e => setProjectForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="Jane Doe, CFO" data-testid="project-contact-name" /></div>
                <div><Label className="text-[10px]">Email</Label><Input type="email" value={projectForm.contact_email} onChange={e => setProjectForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="ap@customer.com" data-testid="project-contact-email" /></div>
                <div><Label className="text-[10px]">WhatsApp # (E.164)</Label><Input value={projectForm.contact_phone} onChange={e => setProjectForm(p => ({ ...p, contact_phone: e.target.value }))} placeholder="919876543210" data-testid="project-contact-phone" /></div>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Country code + number, no spaces or +. Leave blank to skip the corresponding channel.</p>
            </div>

            {/* ===== Client / GST (used for invoicing) ===== */}
            <div className="border border-amber-100 rounded-lg p-3 bg-amber-50/40">
              <p className="text-[11px] font-semibold text-slate-700 mb-2">Client & GST — used for Tax / Proforma invoices</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div><Label className="text-[10px]">Legal Client Name</Label><Input value={projectForm.client_name} onChange={e => setProjectForm(p => ({ ...p, client_name: e.target.value }))} placeholder="NITHYA SAI INDUSTRIES LIMITED" data-testid="project-client-name" /></div>
                <div><Label className="text-[10px]">Client GSTIN</Label><Input value={projectForm.client_gstin} onChange={e => setProjectForm(p => ({ ...p, client_gstin: e.target.value.toUpperCase() }))} placeholder="37AABCN9376L1ZR" data-testid="project-client-gstin" /></div>
                <div><Label className="text-[10px]">Client State</Label><Input value={projectForm.client_state} onChange={e => setProjectForm(p => ({ ...p, client_state: e.target.value }))} placeholder="Andhra Pradesh" data-testid="project-client-state" /></div>
                <div><Label className="text-[10px]">State Code (2-digit)</Label><Input value={projectForm.client_state_code} onChange={e => setProjectForm(p => ({ ...p, client_state_code: e.target.value.slice(0, 2) }))} placeholder="37" data-testid="project-client-state-code" /></div>
                <div className="sm:col-span-2"><Label className="text-[10px]">Billing Address</Label><Textarea value={projectForm.client_address} onChange={e => setProjectForm(p => ({ ...p, client_address: e.target.value }))} rows={2} placeholder="Full address for the invoice" data-testid="project-client-address" /></div>
                <div><Label className="text-[10px]">Client Phone</Label><Input value={projectForm.client_phone} onChange={e => setProjectForm(p => ({ ...p, client_phone: e.target.value }))} placeholder="9866911722" data-testid="project-client-phone" /></div>
                <div><Label className="text-[10px]">Client Email</Label><Input type="email" value={projectForm.client_email} onChange={e => setProjectForm(p => ({ ...p, client_email: e.target.value }))} placeholder="accounts@client.com" data-testid="project-client-email" /></div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px]">GST Category</Label>
                  <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={projectForm.gst_category || "B2B"} onChange={e => setProjectForm(p => ({ ...p, gst_category: e.target.value }))} data-testid="project-gst-category">
                    <option value="B2B">B2B — 18% GST (CGST+SGST or IGST)</option>
                    <option value="B2C">B2C — 0% GST</option>
                    <option value="Export">Export — 0% GST</option>
                  </select>
                </div>
              </div>
            </div>

            {/* ===== Billing Lines Editor ===== */}
            <div className="border rounded-lg p-3 bg-slate-50/60">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-xs font-semibold">Billing Lines</Label>
                  <p className="text-[10px] text-slate-500">Add one line per billable item — e.g. Implementation (One-time), Cloud (Monthly), AMC (Annually).</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px] gap-1"
                  onClick={() => setProjectForm(pf => ({
                    ...pf,
                    billing_lines: [...(pf.billing_lines || []), { id: `new-${Date.now()}`, type: "Others", frequency: "one_time", amount: 0, start_date: pf.start_date || "", notes: "" }],
                  }))}
                  data-testid="billing-line-add-btn"
                >
                  <Plus className="w-3 h-3" /> Add Line
                </Button>
              </div>
              {(projectForm.billing_lines || []).length === 0 ? (
                <p className="text-[11px] text-slate-400 italic px-2 py-3 text-center">No billing lines yet — click &quot;Add Line&quot; to configure.</p>
              ) : (
                <div className="space-y-2">
                  <div className="hidden sm:grid grid-cols-[repeat(14,minmax(0,1fr))] gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 px-1">
                    <span className="col-span-3">Type</span>
                    <span className="col-span-2">Frequency</span>
                    <span className="col-span-2">Amount (₹)</span>
                    <span className="col-span-3">Start Date</span>
                    <span className="col-span-3">End Date <span className="normal-case text-slate-400">(optional)</span></span>
                    <span className="col-span-1"></span>
                  </div>
                  {projectForm.billing_lines.map((bl, idx) => {
                    const dateErr = validateBillingStart(bl.frequency, bl.start_date);
                    const endErr = validateBillingEnd(bl.start_date, bl.end_date, bl.frequency);
                    return (
                    <div key={bl.id || idx} className={`grid grid-cols-12 sm:grid-cols-[repeat(14,minmax(0,1fr))] gap-2 items-start rounded border p-2 ${(dateErr || endErr) ? "bg-red-50 border-red-200" : "bg-white"}`} data-testid={`billing-line-row-${idx}`}>
                      <select
                        className="col-span-12 sm:col-span-3 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                        value={bl.type}
                        onChange={e => { const newType = e.target.value; setProjectForm(pf => ({ ...pf, billing_lines: pf.billing_lines.map((x, i) => i === idx ? { ...x, type: newType, frequency: defaultFrequencyForType(newType, x.frequency) } : x) })); }}
                        data-testid={`billing-line-type-${idx}`}
                      >
                        {BILLING_TYPES.filter(t => t.toLowerCase() !== "amc" || (projectForm.status || "active") === "completed" || (bl.type || "").toLowerCase() === "amc").map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <select
                        className="col-span-6 sm:col-span-2 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white"
                        value={bl.frequency}
                        onChange={e => setProjectForm(pf => ({ ...pf, billing_lines: pf.billing_lines.map((x, i) => i === idx ? { ...x, frequency: e.target.value } : x) }))}
                        data-testid={`billing-line-freq-${idx}`}
                      >
                        {allowedFrequenciesForType(bl.type).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                      <Input
                        type="text"
                        inputMode="numeric"
                        className="col-span-6 sm:col-span-2 h-8 text-xs font-mono"
                        value={bl.amount ? String(bl.amount) : ""}
                        onFocus={(e) => e.target.select()}
                        onChange={e => { const c = sanitizeNumeric(e.target.value); setProjectForm(pf => ({ ...pf, billing_lines: pf.billing_lines.map((x, i) => i === idx ? { ...x, amount: c === "" ? 0 : parseFloat(c) } : x) })); }}
                        placeholder={`Amount (${projectForm.currency === "USD" ? "$" : "₹"})`}
                        data-testid={`billing-line-amount-${idx}`}
                      />
                      <div className="col-span-6 sm:col-span-3">
                        <Input
                          type="date"
                          className={`h-9 text-sm w-full px-2 ${dateErr ? "border-red-400" : ""}`}
                          value={bl.start_date || ""}
                          onChange={e => setProjectForm(pf => ({ ...pf, billing_lines: pf.billing_lines.map((x, i) => i === idx ? { ...x, start_date: e.target.value } : x) }))}
                          data-testid={`billing-line-start-${idx}`}
                        />
                        {dateErr && <p className="text-[10px] text-red-600 mt-0.5" data-testid={`billing-line-error-${idx}`}>{dateErr}</p>}
                      </div>
                      <div className="col-span-6 sm:col-span-3">
                        <Input
                          type="date"
                          className={`h-9 text-sm w-full px-2 ${endErr ? "border-red-400" : ""}`}
                          value={bl.end_date || ""}
                          onChange={e => setProjectForm(pf => ({ ...pf, billing_lines: pf.billing_lines.map((x, i) => i === idx ? { ...x, end_date: e.target.value } : x) }))}
                          data-testid={`billing-line-end-${idx}`}
                          title="Optional end date — no billing accrues after this date"
                        />
                        {endErr && <p className="text-[10px] text-red-600 mt-0.5" data-testid={`billing-line-end-error-${idx}`}>{endErr}</p>}
                      </div>
                      {projectForm.currency === "USD" && bl.amount > 0 && (
                        <p className="col-span-12 sm:[grid-column:span_14/span_14] text-[10px] text-slate-500 pl-1 -mt-1">
                          <span className="text-slate-400">${new Intl.NumberFormat("en-US").format(bl.amount)}</span>
                          <span className="mx-1.5 text-slate-300">→</span>
                          <span className="text-[#0066CC] font-medium">₹{new Intl.NumberFormat("en-IN").format(Math.round(bl.amount * defaultFxRate))}</span>
                          <span className="text-slate-400 ml-1">@ ₹{defaultFxRate}</span>
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="col-span-6 sm:col-span-1 h-8 w-8 p-0 text-red-500 justify-self-end"
                        onClick={() => setProjectForm(pf => ({ ...pf, billing_lines: pf.billing_lines.filter((_, i) => i !== idx) }))}
                        title="Remove line"
                        data-testid={`billing-line-remove-${idx}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs">Purchase Order URL <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={projectForm.purchase_order_url}
                  onChange={e => setProjectForm(p => ({ ...p, purchase_order_url: e.target.value.trim() }))}
                  placeholder="https://onedrive.live.com/... or ftp://server/po.pdf"
                  data-testid="project-po-url-input"
                />
                {projectForm.purchase_order_url && /^https?:\/\//.test(projectForm.purchase_order_url) && (
                  <Button type="button" variant="outline" size="icon" onClick={() => window.open(projectForm.purchase_order_url, "_blank", "noopener")} title="Open PO" data-testid="project-po-preview-btn">
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Paste any link — OneDrive, Google Drive, FTP, or a direct PDF/image URL. Use the eye icon to preview before saving.</p>
            </div>
            <Button onClick={handleSaveProject} disabled={saving} className="w-full bg-[#0066CC] hover:bg-[#0052A3]">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} {editingProject ? "Update" : "Create"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry Dialog (Weekly) */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent className="max-w-[400px]"><DialogHeader><DialogTitle>{entryForm.id ? "Edit Collection Plan Entry" : "Add Collection Plan Entry"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Project *</Label><ProjectSearch projects={projects.filter(p => p.status === "active")} value={entryForm.project_id} onChange={id => setEntryForm(p => ({ ...p, project_id: id, billing_line_id: "" }))} /></div>
            {entryForm.project_id && (() => { const proj = projects.find(p => p.id === entryForm.project_id); return proj ? <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">Pending due: <strong className="text-red-600">{fmt(proj.outstanding || 0)}</strong></p> : null; })()}
            <div>
              <Label className="text-xs">Billing Line (optional)</Label>
              {(() => {
                const selProj = projects.find(p => p.id === entryForm.project_id);
                const lines = selProj?.billing_lines || [];
                return (
                  <select
                    data-testid="entry-billing-line-select"
                    value={entryForm.billing_line_id || ""}
                    onChange={e => setEntryForm(p => ({ ...p, billing_line_id: e.target.value }))}
                    className="h-9 w-full text-sm border border-slate-200 rounded-md px-2 bg-white"
                    disabled={!entryForm.project_id}
                  >
                    <option value="">— Not linked —</option>
                    {lines.map(ln => (
                      <option key={ln.id} value={ln.id}>
                        {ln.type} · {ln.frequency.replace("_", " ")} · ₹{Number(ln.amount || 0).toLocaleString("en-IN")}
                      </option>
                    ))}
                  </select>
                );
              })()}
              <p className="text-[10px] text-slate-400 mt-1">Tag which billing line this plan targets (Cloud/AMC/AI Tool/etc.)</p>
            </div>
            <div><Label className="text-xs">Date *</Label><Input type="date" value={entryForm.date} onChange={e => setEntryForm(p => ({ ...p, date: e.target.value }))} /></div>
            <div><Label className="text-xs">Planned Amount (INR) *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="pl-9 h-11 text-base font-medium"
                  value={entryForm.planned_amount ? String(entryForm.planned_amount) : ""}
                  onFocus={(e) => e.target.select()}
                  onChange={e => { const c = sanitizeNumeric(e.target.value); setEntryForm(p => ({ ...p, planned_amount: c === "" ? 0 : parseInt(c, 10) })); }}
                  placeholder="0"
                  data-testid="entry-planned-amount-input"
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((entryForm.planned_amount || 0) / 100000).toFixed(2)} L</p>
            </div>
            <div><Label className="text-xs">Notes</Label><Input value={entryForm.notes} onChange={e => setEntryForm(p => ({ ...p, notes: e.target.value }))} /></div>
            <Button onClick={handleSaveEntry} disabled={saving} className="w-full bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-entry-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (entryForm.id ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)} {entryForm.id ? "Save Changes" : "Add Entry"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog removed — see inline form on the Receipts tab */}

      {/* Weekly Closing Report Dialog */}
      <ClosingReport report={closingReport} open={!!closingReport} onClose={() => setClosingReport(null)} />

      {/* Past Closing Reports Browser */}
      <Dialog open={showPastReports} onOpenChange={setShowPastReports}>
        <DialogContent className="max-w-[720px] max-h-[80vh] overflow-y-auto" data-testid="past-reports-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" /> Past Weekly Closing Reports
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Click any row to open its detailed snapshot. You can then export it as PDF or PNG.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            {pastReports.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400 italic" data-testid="past-reports-empty">
                No past closing reports yet. Reports are auto-generated when you close a week.
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="past-reports-table">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider">
                    <th className="text-left py-2 px-2 font-semibold">Week</th>
                    <th className="text-right py-2 px-2 font-semibold">Planned</th>
                    <th className="text-right py-2 px-2 font-semibold">Collected</th>
                    <th className="text-right py-2 px-2 font-semibold">Carried</th>
                    <th className="text-center py-2 px-2 font-semibold">Entries</th>
                    <th className="text-right py-2 px-2 font-semibold">Closed On</th>
                  </tr>
                </thead>
                <tbody>
                  {pastReports.map((r) => (
                    <tr
                      key={r.id || r.week_start}
                      className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer"
                      onClick={() => openPastReport(r.week_start)}
                      data-testid={`past-report-row-${r.week_start}`}
                    >
                      <td className="py-2 px-2 font-medium text-slate-800">
                        {new Date(r.week_start + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                        {" – "}
                        {new Date(r.week_end + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className="py-2 px-2 text-right text-slate-700">₹{(((r.totals?.planned) || 0) / 100000).toFixed(2)} L</td>
                      <td className="py-2 px-2 text-right text-emerald-600 font-medium">₹{(((r.totals?.collected) || 0) / 100000).toFixed(2)} L</td>
                      <td className="py-2 px-2 text-right text-amber-600">₹{(((r.totals?.carried) || 0) / 100000).toFixed(2)} L</td>
                      <td className="py-2 px-2 text-center text-slate-600">
                        <span className="text-emerald-600 font-medium">{r.counts?.fully_received || 0}</span>
                        <span className="text-slate-400"> / {r.counts?.total_entries || 0}</span>
                      </td>
                      <td className="py-2 px-2 text-right text-[10px] text-slate-400">
                        {r.closed_at ? new Date(r.closed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button variant="outline" onClick={() => setShowPastReports(false)} data-testid="close-past-reports-btn">Close</Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Bank Statement Mapping Dialog */}
      <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
        <DialogContent className="max-w-[860px] max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Bank Statement Mapping</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mb-3">{bankLines.length} lines loaded. Map each amount to a project. Optionally pick which billing line it belongs to. Auto-suggested projects are pre-filled.</p>
          <div className="space-y-2">
            {bankLines.map(l => {
              const selProjId = bankMappings[l.id] || "";
              const selProj = projects.find(p => p.id === selProjId);
              const projLines = selProj?.billing_lines || [];
              return (
                <div key={l.id} className="flex items-center gap-2 p-2 rounded border bg-slate-50 text-xs flex-wrap">
                  <span className="w-20 shrink-0 font-medium">{l.date}</span>
                  <span className="w-20 shrink-0 font-bold text-emerald-700">{fmt(l.amount)}</span>
                  <span className="w-32 shrink-0 text-slate-500 truncate" title={l.reference}>{l.reference}</span>
                  <select className="flex-1 min-w-[160px] h-8 rounded border text-xs px-2" value={selProjId}
                    onChange={e => setBankMappings(p => ({ ...p, [l.id]: e.target.value }))}
                    data-testid={`bank-line-project-${l.id}`}>
                    <option value="">-- Select Project --</option>
                    {projects.filter(p => p.status === "active").map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <select className="flex-1 min-w-[160px] h-8 rounded border text-xs px-2" value={bankBillingLineMappings[l.id] || ""}
                    onChange={e => setBankBillingLineMappings(p => ({ ...p, [l.id]: e.target.value }))}
                    disabled={!selProjId}
                    data-testid={`bank-line-billing-${l.id}`}>
                    <option value="">— Billing line —</option>
                    {projLines.map(ln => (
                      <option key={ln.id} value={ln.id}>
                        {ln.type} · {ln.frequency.replace("_", " ")} · ₹{Number(ln.amount || 0).toLocaleString("en-IN")}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
          <Button onClick={handleBankMap} disabled={saving} className="w-full bg-[#0066CC] hover:bg-[#0052A3] mt-4">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />} Map &amp; Create Receipts</Button>
        </DialogContent>
      </Dialog>

      {/* Historical Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-[400px]"><DialogHeader><DialogTitle>Import Historical Receipts</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-slate-500">Upload an Excel file with columns: <strong>Date, Project Name, Amount, Bank Reference</strong></p>
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Project names must match exactly with existing projects in the system.</p>
            <a href={`${api.defaults.baseURL}/collections/templates/receipts`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full gap-2 mb-2"><Download className="w-4 h-4" /> Download Receipt Template</Button>
            </a>
            <label className="block"><Button variant="outline" className="w-full gap-2 cursor-pointer" asChild><span><Upload className="w-4 h-4" /> Select Excel File to Import</span></Button><input type="file" accept=".xlsx" className="hidden" onChange={handleHistoricalImport} /></label>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Reminder Dialog */}
      <Dialog open={reminderDialog.open} onOpenChange={(v) => setReminderDialog({ ...reminderDialog, open: v })}>
        <DialogContent className="max-w-md" data-testid="reminder-dialog">
          <DialogHeader><DialogTitle>Send Reminder — {reminderDialog.project?.name}</DialogTitle></DialogHeader>
          {reminderDialog.project && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 p-2 rounded bg-slate-50 text-[11px]">
                <div><span className="text-slate-500">Contact:</span> <strong className="text-slate-800">{reminderDialog.project.contact_name || "—"}</strong></div>
                <div><span className="text-slate-500">Outstanding:</span> <strong className="text-red-600">₹{fmt(reminderDialog.project.outstanding || 0)}</strong></div>
                <div><span className="text-slate-500">Email:</span> <span className="text-slate-700 truncate">{reminderDialog.project.contact_email || "—"}</span></div>
                <div><span className="text-slate-500">Phone:</span> <span className="text-slate-700">{reminderDialog.project.contact_phone || "—"}</span></div>
              </div>
              <div>
                <Label className="text-xs">Channel</Label>
                <div className="flex gap-2 mt-1">
                  {["email", "whatsapp"].map(c => (
                    <button
                      key={c}
                      data-testid={`reminder-channel-${c}`}
                      onClick={() => {
                        const firstTpl = reminderTemplates.find(t => t.channel === c);
                        setReminderDialog(d => ({ ...d, channel: c, template_id: firstTpl?.id || "" }));
                      }}
                      className={`flex-1 py-2 text-xs rounded border ${reminderDialog.channel === c ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white border-slate-200 text-slate-600"}`}
                    >
                      {c === "email" ? <Mail className="w-3.5 h-3.5 inline mr-1" /> : <MessageCircle className="w-3.5 h-3.5 inline mr-1" />}
                      {c === "email" ? "Email" : "WhatsApp"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Template *</Label>
                <select
                  data-testid="reminder-template-select"
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                  value={reminderDialog.template_id}
                  onChange={e => setReminderDialog({ ...reminderDialog, template_id: e.target.value })}
                >
                  <option value="">— select —</option>
                  {reminderTemplates.filter(t => t.channel === reminderDialog.channel).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {reminderTemplates.filter(t => t.channel === reminderDialog.channel).length === 0 && (
                  <p className="text-[10px] text-amber-600 mt-1">No {reminderDialog.channel} templates. Create one in Settings → Automated Reminders.</p>
                )}
              </div>
              {(reminderDialog.project.billing_lines || []).length > 1 && (
                <div>
                  <Label className="text-xs">Specific Billing Line (optional)</Label>
                  <select
                    data-testid="reminder-line-select"
                    className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                    value={reminderDialog.billing_line_id}
                    onChange={e => setReminderDialog({ ...reminderDialog, billing_line_id: e.target.value })}
                  >
                    <option value="">— all overdue lines —</option>
                    {reminderDialog.project.billing_lines.map(l => (
                      <option key={l.id} value={l.id}>{l.type} · {l.frequency.replace("_", " ")} · ₹{fmt(l.amount)}</option>
                    ))}
                  </select>
                </div>
              )}
              {reminderDialog.channel === "email" && (
                <div>
                  <Label className="text-xs">Override Email (optional)</Label>
                  <Input value={reminderDialog.override_email} onChange={e => setReminderDialog({ ...reminderDialog, override_email: e.target.value })} placeholder={reminderDialog.project.contact_email || "cfo@customer.com"} data-testid="reminder-email-input" />
                </div>
              )}
              {reminderDialog.channel === "whatsapp" && (
                <div>
                  <Label className="text-xs">Override Phone (optional, E.164 w/o +)</Label>
                  <Input value={reminderDialog.override_phone} onChange={e => setReminderDialog({ ...reminderDialog, override_phone: e.target.value })} placeholder={reminderDialog.project.contact_phone || "919876543210"} data-testid="reminder-phone-input" />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="ghost" onClick={() => setReminderDialog({ ...reminderDialog, open: false })}>Cancel</Button>
                <Button onClick={sendManualReminder} disabled={sendingReminder || !reminderDialog.template_id} className="bg-[#0066CC] gap-1.5" data-testid="reminder-send-btn">
                  {sendingReminder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send Now
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Hidden ERP-style Statement Document — rendered off-screen and used by PDF generator */}
      {snapshotData && (
        <StatementDocument
          data={snapshotData}
          project={projects.find(p => p.id === snapshotData.id)}
          absolinLogoUrl={LOGO_URL}
        />
      )}

      {/* Project Snapshot Dialog */}
      <Dialog open={showSnapshot} onOpenChange={setShowSnapshot}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto" data-testid="snapshot-dialog">
          <DialogHeader className="sr-only">
            <DialogTitle>{snapshotData?.name ? `Project Snapshot — ${snapshotData.name}` : "Project Snapshot"}</DialogTitle>
            <DialogDescription>Detailed financial snapshot with billing cycle and receipts.</DialogDescription>
          </DialogHeader>
          {snapshotData && (
            <div id="snapshot-card" className="bg-white">
              {/* Header — dark banner */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <img src={LOGO_URL} alt="Absolin" className="h-6 brightness-200" />
                  <span className="text-[10px] text-slate-400 uppercase tracking-wider">Project Snapshot · as on {snapshotData.as_on}</span>
                </div>
                <h2 className="text-xl font-bold mb-1" data-testid="snapshot-project-name">{snapshotData.name}</h2>
                <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                  <span className="capitalize">{snapshotData.project_type?.replace(/_/g, " ")}</span>
                  <span>•</span>
                  <span className="capitalize">{snapshotData.status}</span>
                  {snapshotData.order_date && <><span>•</span><span>Order: {snapshotData.order_date}</span></>}
                  {snapshotData.start_date && <><span>•</span><span>Start: {snapshotData.start_date}</span></>}
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-base font-bold text-[#4d9dff] tabular-nums" data-testid="snapshot-expected">{fmtFull(snapshotData.expected_billed || snapshotData.project_amount)}</p>
                    <p className="text-[10px] text-slate-400">Expected Billed</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-base font-bold text-emerald-400 tabular-nums" data-testid="snapshot-collected">{fmtFull(snapshotData.total_collected)}</p>
                    <p className="text-[10px] text-slate-400">Collected</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-base font-bold text-red-400 tabular-nums" data-testid="snapshot-outstanding">{fmtFull(snapshotData.outstanding)}</p>
                    <p className="text-[10px] text-slate-400">Outstanding</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-base font-bold text-amber-300 tabular-nums">{snapshotData.collection_pct}%</p>
                    <p className="text-[10px] text-slate-400">{snapshotData.receipt_count} receipt{snapshotData.receipt_count !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(100, snapshotData.collection_pct || 0)}%` }} />
                </div>

                {/* Contract subline */}
                {(snapshotData.contract_one_time > 0 || snapshotData.contract_recurring_per_period > 0) && (
                  <p className="text-[11px] text-slate-400 mt-3">
                    Contract:
                    {snapshotData.contract_one_time > 0 && <span className="ml-2">one-time <strong className="text-white">{fmtFull(snapshotData.contract_one_time)}</strong></span>}
                    {snapshotData.contract_recurring_per_period > 0 && <span className="ml-2">recurring <strong className="text-white">{fmtFull(snapshotData.contract_recurring_per_period)}</strong>/period</span>}
                  </p>
                )}
                {snapshotData.notes && <p className="text-[11px] text-slate-400 mt-2 italic">Note: {snapshotData.notes}</p>}
                {snapshotData.purchase_order_url && (
                  <a href={snapshotData.purchase_order_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#4d9dff] underline mt-2 inline-block">View Purchase Order →</a>
                )}
              </div>

              {/* Billing Cycle Table */}
              <div className="p-4 sm:p-6 border-b border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-800">Billing Cycle</h3>
                  <span className="text-[11px] text-slate-500">{(snapshotData.billing_lines || []).length} line{(snapshotData.billing_lines || []).length !== 1 ? "s" : ""}</span>
                </div>
                {(!snapshotData.billing_lines || snapshotData.billing_lines.length === 0) ? (
                  <p className="text-xs text-slate-400 italic text-center py-4">No billing lines configured.</p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-xs" data-testid="snapshot-billing-table">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-2.5 font-semibold text-slate-600">Type</th>
                          <th className="text-left p-2.5 font-semibold text-slate-600">Frequency</th>
                          <th className="text-right p-2.5 font-semibold text-slate-600">Amount</th>
                          <th className="text-left p-2.5 font-semibold text-slate-600 hidden sm:table-cell">Start</th>
                          <th className="text-right p-2.5 font-semibold text-slate-600 hidden sm:table-cell">Periods</th>
                          <th className="text-right p-2.5 font-semibold text-slate-600">Expected</th>
                          <th className="text-right p-2.5 font-semibold text-slate-600">Collected</th>
                          <th className="text-right p-2.5 font-semibold text-slate-600">Outstanding</th>
                          <th className="text-left p-2.5 font-semibold text-slate-600 hidden md:table-cell">Next Bill</th>
                        </tr>
                      </thead>
                      <tbody>
                        {snapshotData.billing_lines.map(l => {
                          const freqLbl = ({ one_time: "One-time", monthly: "Monthly", quarterly: "Quarterly", annually: "Annually" }[l.frequency] || l.frequency);
                          return (
                            <tr key={l.id} className="border-t border-slate-100" data-testid={`snapshot-line-${l.id}`}>
                              <td className="p-2.5">
                                <span className="text-[11px] font-semibold bg-blue-50 text-[#0066CC] px-1.5 py-0.5 rounded">{l.type}</span>
                              </td>
                              <td className="p-2.5 text-slate-600">{freqLbl}</td>
                              <td className="p-2.5 text-right text-slate-800 font-mono">{fmtFull(l.amount)}</td>
                              <td className="p-2.5 text-slate-500 text-[11px] hidden sm:table-cell">{l.start_date || "—"}</td>
                              <td className="p-2.5 text-right text-slate-500 hidden sm:table-cell">{l.completed_periods}</td>
                              <td className="p-2.5 text-right font-semibold text-[#0066CC]">{fmtFull(l.expected)}</td>
                              <td className="p-2.5 text-right font-semibold text-emerald-600">{fmtFull(l.collected)}</td>
                              <td className={`p-2.5 text-right font-semibold ${l.outstanding > 0 ? "text-red-600" : "text-slate-400"}`}>{fmtFull(l.outstanding)}</td>
                              <td className="p-2.5 text-slate-500 text-[11px] hidden md:table-cell">{l.next_bill_date || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-slate-50 font-semibold">
                        <tr className="border-t border-slate-200">
                          <td className="p-2.5 text-slate-700" colSpan={5}>Totals</td>
                          <td className="p-2.5 text-right text-[#0066CC]">{fmtFull(snapshotData.expected_billed)}</td>
                          <td className="p-2.5 text-right text-emerald-700">{fmtFull(snapshotData.total_collected)}</td>
                          <td className={`p-2.5 text-right ${snapshotData.outstanding > 0 ? "text-red-700" : "text-slate-400"}`}>{fmtFull(snapshotData.outstanding)}</td>
                          <td className="p-2.5 hidden md:table-cell" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              <p className="text-[9px] text-slate-400 mt-2 mb-3 text-center">Generated by Absolin Collection Planning · {snapshotData.generated_at?.slice(0, 10)}</p>
            </div>
          )}

          {/* All Receipts (outside the snapshot image, but scrollable) */}
          {snapshotData?.all_receipts?.length > 0 && (
            <div className="p-4 sm:p-6 bg-white border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800">All Receipts ({snapshotData.all_receipts.length})</h3>
                <span className="text-[11px] text-slate-500">Total: <strong className="text-emerald-600">{fmtFull(snapshotData.total_collected)}</strong></span>
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs" data-testid="snapshot-receipts-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2.5 font-semibold text-slate-600">Receipt Date</th>
                      <th className="text-left p-2.5 font-semibold text-slate-600">Billing Line</th>
                      <th className="text-right p-2.5 font-semibold text-slate-600">Amount</th>
                      <th className="text-left p-2.5 font-semibold text-slate-600 hidden sm:table-cell">Bank Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshotData.all_receipts.map(r => (
                      <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-2.5 text-slate-700 whitespace-nowrap">{r.receipt_date}</td>
                        <td className="p-2.5 text-slate-600">
                          {r.billing_type ? (
                            <span className="text-[11px] font-semibold bg-blue-50 text-[#0066CC] px-1.5 py-0.5 rounded">
                              {r.billing_type}{r.billing_frequency ? ` · ${r.billing_frequency.replace("_", " ")}` : ""}
                            </span>
                          ) : <span className="text-slate-300 text-[10px]">Untagged</span>}
                        </td>
                        <td className="p-2.5 text-right font-semibold text-emerald-600 tabular-nums">{fmtFull(r.collection_amount)}</td>
                        <td className="p-2.5 text-slate-500 text-[11px] hidden sm:table-cell">{r.bank_reference || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="p-3 bg-white flex gap-2 justify-end sticky bottom-0 border-t border-slate-100">
            <Button variant="outline" size="sm" onClick={() => setShowSnapshot(false)} data-testid="snapshot-close-btn">Close</Button>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="snapshot-save-pdf-btn" onClick={() => {
              if (snapshotData) _renderStatementToPdf(snapshotData.name);
            }}><FileText className="w-3.5 h-3.5" /> Save as PDF</Button>
            <Button size="sm" className="bg-[#0066CC] gap-1.5" data-testid="snapshot-save-image-btn" onClick={() => {
              const el = document.getElementById("snapshot-card");
              if (el) { import("html2canvas").then(mod => mod.default(el, { scale: 2, backgroundColor: "#ffffff" }).then(c => { const link = document.createElement("a"); link.download = `${snapshotData?.name || "project"}_snapshot.png`; link.href = c.toDataURL(); link.click(); })); }
            }}><Camera className="w-3.5 h-3.5" /> Save as Image</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Projects Dialog */}
      <ProjectsBulkEditDialog open={showBulkEdit} onOpenChange={setShowBulkEdit} projects={projects} onSaved={loadProjects} />

      {/* Data Eraser Confirmation Dialog */}
      <Dialog open={eraseConfirmOpen} onOpenChange={(o) => !erasing && setEraseConfirmOpen(o)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Confirm Permanent Erase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 text-sm text-slate-700">
            <p>
              You are about to permanently delete <span className="font-bold text-red-700">{erasePreview?.count || 0}</span> receipt{(erasePreview?.count || 0) === 1 ? "" : "s"} totalling
              <span className="font-bold text-red-700"> ₹{fmt(erasePreview?.total_amount || 0)}</span> recorded between <span className="font-semibold">{fmtDateLong(erasePreview?.start_date)}</span> and <span className="font-semibold">{fmtDateLong(erasePreview?.end_date)}</span>.
            </p>
            <p className="text-xs text-red-600">This action cannot be undone.</p>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="ghost" disabled={erasing} onClick={() => setEraseConfirmOpen(false)} data-testid="eraser-cancel-btn">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={erasing} onClick={handleEraseConfirm} data-testid="eraser-confirm-btn">
              {erasing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Yes, Erase Permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Date Range Presets (India FY = Apr 1 — Mar 31) =====
